import { v4 as uuid } from "uuid";
import { ElementSource } from "chq";
import { IMAGE_TYPE } from "components/stories/video-studio/utils/constants";
import { findAllGenericElements } from "../../../genericTemplate/utils/findGenericElementByName";
import getTimeDifference from "./getTimeDifference";
import { SelectedMedia } from "../genericMontageTemplate";

interface NewStoryElement {
  id: string;
  selectedVisual: SelectedMedia;
  source: ElementSource;
}

const newRelativeElement = (
  relativeElement: ElementSource,
  newStoryElementId: string,
  newStoryElement: ElementSource,
  storyNewTime: number
) => ({
  ...relativeElement,
  name: relativeElement.name.replace(/RT=[^@]+/, `RT=${newStoryElementId}`),
  time: storyNewTime + (relativeElement.time - newStoryElement.source.time),
  id: uuid()
});

const storyElement = (
  newStoryElement: ElementSource,
  newStoryElementId: string,
  storyNewTime: number,
  duration: number
) => ({
  ...newStoryElement.source,
  id: newStoryElementId,
  source: newStoryElement.selectedVisual.media.url,
  type: newStoryElement.selectedVisual.media.mediaType,
  time: storyNewTime,
  duration
});

const createNewStoryElement = (
  templateElement: ElementSource,
  selectedVisual: SelectedMedia
): NewStoryElement => ({
  id: templateElement.id,
  selectedVisual,
  source: templateElement
});

const getUpdatedTemplateElements = (elements: ElementSource[], selectedMedia: SelectedMedia[]) => {
  const { storyElement: storyElements } = findAllGenericElements(elements);

  const lastStoryElement = storyElements.reduce((max, current) => (
    current.time > max.time ? current : max
  ), storyElements[0]);

  const sortedStoryElements = [...storyElements].sort((a, b) => a.time - b.time);
  const timeDifference = getTimeDifference(sortedStoryElements);

  const newStoryElements = selectedMedia.map((selectedVisual, index) => (
    index >= storyElements.length
      ? createNewStoryElement(storyElements[index % storyElements.length], selectedVisual)
      : null
  )).filter((element): element is NewStoryElement => element !== null);

  let currentTime = lastStoryElement.time + lastStoryElement.duration;
  const newElements = newStoryElements.map(newStoryElement => {
    const relativeElements = elements.filter((element: ElementSource) => (
      element.name.includes(newStoryElement.id)
    ));
    const newStoryElementId = uuid();
    const duration = newStoryElement.selectedVisual.media.mediaType === IMAGE_TYPE
      ? 5 : (newStoryElement.selectedVisual.media.duration ?? 0);
    const storyNewTime = currentTime + timeDifference[newStoryElement.id];
    if (!relativeElements.length) {
      const result = [storyElement(newStoryElement, newStoryElementId, storyNewTime, duration)];
      currentTime += timeDifference[newStoryElement.id] + duration;
      return result;
    }
    const result = [
      ...relativeElements.map((relativeElement: ElementSource) => (
        newRelativeElement(relativeElement, newStoryElementId, newStoryElement, storyNewTime)
      )),
      storyElement(newStoryElement, newStoryElementId, storyNewTime, duration)
    ];
    currentTime += timeDifference[newStoryElement.id] + duration;
    return result;
  });

  return [...elements, ...newElements.flat()];
};

export default getUpdatedTemplateElements;
